/**
 * Mobile Lot Card Plugin for Native Interactive Site Map
 *
 * Displays a mobile lot card overlay at the bottom when in full-screen lot card view.
 *
 * Plugin lifecycle:
 * - Only activates on mobile (`context.state.isMobile`)
 * - Shows card when: activeView === VIEW_MOBILE_LOT_CARD
 * - Hides when activeView changes to anything else
 * - Uses pre-rendered container element from template
 * - Positions card absolutely at the bottom of the screen (via SCSS class)
 * - Hides entire drawer when card is active
 * - Back button positioned top-left
 *
 * @module nativeInteractiveSiteMap/plugins/mobileLotCard.plugin
 */

import { distinctUntilChanged, filter, map, pairwise } from 'rxjs/operators';
import createMobileLotCardDom, { createMobileLotCardBackButton } from '../components/mobileLotCard.dom';
import createMobileLotCardInteractionHandlers from '../handlers/mobileLotCardInteractionHandlers';
import createHeaderService from '../services/headerService';
import {
  selectCurrentlySelectedLot,
  selectActiveView,
  selectSelectedLotId,
  selectShouldDisplayLotGroup,
} from '../redux/selectors';
import { VIEW_FLOOR_PLAN_DETAIL, VIEW_MOBILE_LOT_CARD } from '../constants';

export default function createMobileLotCardPlugin() {
  const state = {
    cardContainer: null, // Container for card (pre-rendered in template with [data-mobile-lot-card-container])
    cardComponent: null, // The actual lot card component
    backButton: null, // The back button (top-left)
    store$: null,
    getState: null,
    dispatch: null,
    dataActions: null,
    interactionHandlers: null,
    headerService: null,
    subscriptions: [],
    isMobile: false,
  };

  /**
   * Mounts card and back button to container
   */
  function mountCardAndButton(lot, showLotGroup) {
    // Create and mount card as child of container
    state.cardComponent = createMobileLotCardDom(
      lot,
      state.interactionHandlers.onReturnToDetails,
      showLotGroup
    );
    state.cardContainer.appendChild(state.cardComponent.getElement());

    // Create and mount back button as sibling (after container)
    state.backButton = createMobileLotCardBackButton(state.interactionHandlers.onReturnToDetails);
    state.cardContainer.parentElement.appendChild(state.backButton.getElement());
  }

  /**
   * Unmounts card and back button from container
   */
  function unmountCardAndButton() {
    // Remove card component and its DOM element
    if (state.cardComponent) {
      state.cardComponent.remove();
      state.cardComponent = null;
    }

    // Remove back button component and its DOM element
    if (state.backButton) {
      state.backButton.remove();
      state.backButton = null;
    }
  }

  /**
   * Updates the card view visibility and content based on Redux state
   */
  function syncCardVisibility() {
    const storeState = state.getState();
    const activeView = selectActiveView(storeState);
    const lot = selectCurrentlySelectedLot(storeState);
    const showLotGroup = selectShouldDisplayLotGroup(storeState);
    const isMobileLotCardView = activeView === VIEW_MOBILE_LOT_CARD;
    const isCurrentlyShowing = !!state.cardComponent;

    if (isMobileLotCardView) {
      state.headerService?.hide();
    } else {
      state.headerService?.show();
    }

    // Entering mobile lot card view
    if (isMobileLotCardView && !isCurrentlyShowing) {
      mountCardAndButton(lot, showLotGroup);
      return;
    }

    // Leaving mobile lot card view
    if (!isMobileLotCardView && isCurrentlyShowing) {
      unmountCardAndButton();
      return;
    }

    // Still in mobile lot card view, refresh card for the newly selected lot
    if (isMobileLotCardView && isCurrentlyShowing) {
      state.cardComponent.updateContent(lot, showLotGroup);
    }
  }

  function shouldSyncCardVisibility([prev, next]) {
    const enteredFromChangeLotFlow =
      prev.activeView === VIEW_FLOOR_PLAN_DETAIL &&
      next.activeView === VIEW_MOBILE_LOT_CARD;
    const currentlyInMobileLotCard = next.activeView === VIEW_MOBILE_LOT_CARD;
    const leavingMobileLotCard = prev.activeView === VIEW_MOBILE_LOT_CARD;

    return enteredFromChangeLotFlow || currentlyInMobileLotCard || leavingMobileLotCard;
  }

  /**
   * Sets up subscriptions to Redux store changes
   */
  function setupSubscriptions() {
    return [
      // Watch activeView and selectedLotId changes (lot data is derived from ID)
      state.store$.pipe(
        map((storeState) => ({
          activeView: selectActiveView(storeState),
          selectedLotId: selectSelectedLotId(storeState),
          showLotGroup: selectShouldDisplayLotGroup(storeState),
        })),
        distinctUntilChanged((prev, next) =>
          prev.activeView === next.activeView &&
          prev.selectedLotId === next.selectedLotId &&
          prev.showLotGroup === next.showLotGroup
        ),
        pairwise(),
        filter(shouldSyncCardVisibility),
      ).subscribe(() => syncCardVisibility()),
    ];
  }

  return {
    name: 'mobile-lot-card',

    async start(context) {
      // Only run on mobile
      if (!context.state.isMobile) {
        return;
      }

      state.isMobile = true;
      state.store$ = context.services.store$;
      state.getState = context.services.getState;
      state.dispatch = context.services.dispatch;
      state.dataActions = context.services.dataActions;
      state.headerService = createHeaderService(context.root.querySelector('[data-header]'));
      state.interactionHandlers = createMobileLotCardInteractionHandlers({
        getState: state.getState,
        dispatch: state.dispatch,
        dataActions: state.dataActions,
      });

      // Find and cache the mobile lot card container (pre-rendered in template)
      const container = context.root.querySelector('[data-mobile-lot-card-element]');
      if (!container) {
        return;
      }
      state.cardContainer = container;

      // Setup subscriptions to sync card visibility/content
      state.subscriptions = setupSubscriptions();

      // Initial sync based on current state
      syncCardVisibility();
    },

    async stop() {
      // Cleanup subscriptions
      state.subscriptions.forEach((sub) => sub.unsubscribe());
      state.subscriptions = [];

      // Remove card and back button
      unmountCardAndButton();

      // Ensure header is restored when plugin stops while mobile card is active
      state.headerService?.show();

      state.cardContainer = null;
      state.store$ = null;
      state.getState = null;
      state.dispatch = null;
      state.dataActions = null;
      state.interactionHandlers = null;
      state.headerService = null;
    },
  };
}

